import React, { useEffect, useState } from 'react';
import Select, { GroupBase } from 'react-select';
// Predefined types
export const Types = {
    ROADS: {
        0: 'DamagedRoadSurface',
        1: 'DamagedPavementSurface',
        2: 'AbandonedVehicle',
        3: 'TrafficLightFailure',
        4: 'DamagedSignPostOrRailing',
        5: 'CollapsedDrain',
        6: 'BrokenStop',
    },
    NATURE: {
        7: 'FallenTree',
        8: 'AnObstructedRoadSign',
        9: 'WildAnimals',
        10: 'DeadAnimal',
    },
    WEATHER: {
        11: 'SlipperyRoadSurface',
        12: 'SlipperyPavement',
        13: 'HangingIcicles',
    },
    LIGHTNING: {
        14: 'DamagedLight',
        15: 'NoLighting',
        16: 'ExposedElectricWire',
    },
    WASTE: {
        17: 'WildDump',
        18: 'BrokenWasteContainer',
        19: 'ScatteredWaste',
    },
    VANDALISM: {
        20: 'AnActOfVandalism',
        21: 'HateSpeechGraffiti',
        22: 'IllegalAdvertising',
    },
    OTHERS: {
        23: 'ScootersScattered',
        24: 'OwnReport',
    },
};
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import {
    FaBolt,
    FaLeaf,
    FaQuestion,
    FaRoad,
    FaSnowflake,
    FaSprayCan,
    FaTrash,
} from 'react-icons/fa';
import { ErrorMessage, FormikProps } from 'formik';

export default function SelectCategory(props: {
    formik: FormikProps<any>;
    onChange: (value: string) => void;
}) {
    const { t } = useTranslation();
    const [categories, setCategories] = useState<
        {
            label: string;
            options: { label: string; value: string; group: string }[];
        }[]
    >([]);

    useEffect(() => {
        // Use static Types
        const categoriesArray: {
            label: string;
            options: { label: string; value: string; group: string }[];
        }[] = [];

        for (const [categoryName, categoryValue] of Object.entries(Types)) {
            const optionsArray: { label: string; value: string; group: string }[] = [];
            for (const [categoryID] of Object.entries(categoryValue)) {
                optionsArray.push({
                    label: t(`translation:REPORT_TYPE.${categoryID}`),
                    value: categoryID,
                    group: categoryName,
                });
            }
            categoriesArray.push({ label: categoryName, options: optionsArray });
        }
        setCategories(categoriesArray);
    }, [t]);

    return (
        <div>
            <Select<{ label: string; value: string; group: string }>
                classNamePrefix={'report-form-select'}
                styles={{
                    control: (baseStyles, state) => {
                        return {
                            ...baseStyles,
                            boxShadow: state.menuIsOpen
                                ? props.formik.errors['category'] &&
                                  props.formik.touched['category']
                                    ? '0 0 0 3px #fca5a5'
                                    : '0 0 0 3px #93c5fd'
                                : '',
                            borderColor:
                                props.formik.errors['category'] && props.formik.touched['category']
                                    ? '#ef4444'
                                    : state.menuIsOpen
                                    ? '#60a5fa'
                                    : '',
                            borderWidth: '1px',
                            ':hover': {
                                borderColor:
                                    props.formik.errors['category'] &&
                                    props.formik.touched['category']
                                        ? '#ef4444'
                                        : '',
                            },
                        };
                    },
                    option: (baseStyles, { data }) => {
                        return {
                            ...baseStyles,
                            alignItems: 'center',
                            display: 'flex',
                            ':before': {
                                backgroundColor: `${data.group === 'ROADS' ? '#1e293b' : ''}${
                                    data.group === 'NATURE' ? '#16a34a' : ''
                                }${data.group === 'LIGHTNING' ? '#f59e0b' : ''}${
                                    data.group === 'WEATHER' ? '#3b82f6' : ''
                                }${data.group === 'WASTE' ? '#7c3aed' : ''}${
                                    data.group === 'VANDALISM' ? '#ef4444' : ''
                                }${data.group === 'OTHERS' ? '#9ca3af' : ''}`,
                                borderRadius: 10,
                                content: '" "',
                                display: 'block',
                                marginRight: 8,
                                height: 10,
                                width: 10,
                            },
                        };
                    },
                }}
                onChange={(value) => {
                    if (value) {
                        props.formik.setFieldValue('category', value.value);
                        props.onChange(value.value);
                    }
                }}
                isSearchable={true}
                className={'w-full'}
                options={categories}
                formatGroupLabel={GroupLabel}
                placeholder={t('translation:REPORT.NEW_REPORT.SELECT_CATEGORY')}
                noOptionsMessage={() => t('translation:REPORT.NEW_REPORT.NO_OPTIONS')}
            />
            <small className={'text-red-500'}>
                <ErrorMessage name={'category'} />
            </small>
        </div>
    );
}

function GroupLabel(group: GroupBase<{ label: string; value: string }>) {
    return (
        <div
            className={`flex items-center gap-2 px-1 pb-1
        ${group.label === 'ROADS' && 'text-slate-800'} 
        ${group.label === 'NATURE' && 'text-green-600'} 
        ${group.label === 'LIGHTNING' && 'text-amber-500'}
        ${group.label === 'WEATHER' && 'text-blue-500'}
        ${group.label === 'WASTE' && 'text-violet-600'}
        ${group.label === 'VANDALISM' && 'text-red-500'}
        ${group.label === 'OTHERS' && 'text-gray-400'}
        `}>
            {group.label === 'ROADS' && <FaRoad className={'text-xl'} />}
            {group.label === 'NATURE' && <FaLeaf className={'text-xl'} />}
            {group.label === 'LIGHTNING' && <FaBolt className={'text-xl'} />}
            {group.label === 'WEATHER' && <FaSnowflake className={'text-xl'} />}
            {group.label === 'WASTE' && <FaTrash className={'text-xl'} />}
            {group.label === 'VANDALISM' && <FaSprayCan className={'text-xl'} />}
            {group.label === 'OTHERS' && <FaQuestion className={'text-xl'} />}
            <span>{i18next.t(`translation:REPORT_CATEGORY.${group.label}`)}</span>
            <span className={'ml-auto'}>{group.options.length}</span>
        </div>
    );
}
